import type { ReactNode } from 'react';
import { cx } from '../cx';

/**
 * A surface with a hairline border. The section title is small, uppercase and
 * muted; the content carries the weight.
 *
 * ALWAYS OPAQUE. A Card carries record values, and translucency under twelve columns
 * of 15px text is where readability dies - which is also what the HIG says about
 * glass in the content layer. Translucency in this product lives on chrome only:
 * the sidebar, dialogs, menus and the command menu, exactly where a native app puts
 * it.
 */
export interface CardProps {
  readonly title?: string | undefined;
  readonly actions?: ReactNode | undefined;
  readonly children: ReactNode;
  readonly padded?: boolean | undefined;
  readonly className?: string | undefined;
}

export function Card({ title, actions, children, padded = true, className }: CardProps) {
  return (
    <section
      className={cx('rounded-surface border border-line bg-surface shadow-raised', className)}
    >
      {(title !== undefined || actions !== undefined) && (
        /**
         * THE HEADER HAS REAL VERTICAL PADDING, and that is a RULE rather than an
         * accident (polish wave 1). It used to be `min-h-11 items-center` and no
         * `py-*` at all: the breathing room around a 40px control was whatever the
         * 44px floor happened to leave over, so the instant a card's actions were
         * taller than 44px they touched the card's own edges - measured at 1px top
         * and bottom on the Members screen, and latent in every Card that takes
         * actions. `min-h-11` stays as the FLOOR a title-only header keeps.
         *
         * AND IT WRAPS, which is the same finding one step on: a header with no
         * fallback layout for wide actions has nowhere to put them but the edge. The
         * actions drop to their own line inside the padding instead, one `--space-2`
         * below the title.
         */
        <header className="flex min-h-11 flex-wrap items-center justify-between gap-x-3 gap-y-2 border-b border-line-subtle px-4 py-2">
          {title !== undefined && (
            <h2 className="text-2xs font-semibold tracking-widest text-ink-muted uppercase">
              {title}
            </h2>
          )}
          {actions}
        </header>
      )}
      <div className={cx(padded && 'p-4')}>{children}</div>
    </section>
  );
}
